import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { newLinkAAskSessionId } from "@/lib/linkAAskCleoSession";
import type { ProposalCleoAudience } from "@/lib/proposalCustomerAiUxFlags";
import { PROPOSAL_TEXT } from "@/lib/proposalTheme";
import { trpc } from "@/lib/trpc";
import { resolveAskCleoProposalItemId, shouldAttachStickyProposalItemId, type ProposalCleoPendingAsk } from "@shared/proposalCleoInProposalExplain";
import { MessageCircle, X } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { toast } from "sonner";

const SALES_TEXT = PROPOSAL_TEXT;

type Props = {
  proposalId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When false, unmount callers should also stop rendering this tree. */
  enabled: boolean;
  /** staff Link A vs customer Link B — customer skips PIC-digest session-end and proposal-wide history. */
  audience?: ProposalCleoAudience;
  /** Staff `?` on a product line — open chat and auto-send the prefilled explain question. */
  pendingAsk?: ProposalCleoPendingAsk | null;
  onPendingAskConsumed?: () => void;
};

/**
 * Staff Link A / customer Link B Ask Cleo chrome for proposal pages.
 * Mount only when `shouldShowProposalCleoAssistantUi` is true.
 * Customer path reuses the same askOnProposal rails; it does not load staff
 * proposal-wide history and does not fire the staff PIC digest on close.
 */
export default function ProposalPublicCleoAssistant({
  proposalId,
  open,
  onOpenChange,
  enabled,
  audience = "staff",
  pendingAsk = null,
  onPendingAskConsumed,
}: Props) {
  const [cleoMessages, setCleoMessages] = useState<Array<{ role: "user" | "assistant"; text: string }>>([]);
  const [cleoInput, setCleoInput] = useState("");
  const cleoScrollRef = useRef<HTMLDivElement | null>(null);
  const historyLoadedRef = useRef(false);
  const sendingKeyRef = useRef<number | null>(null);
  const sessionIdRef = useRef(newLinkAAskSessionId());
  const lastItemIdRef = useRef<number | undefined>(undefined);
  const endedRef = useRef(false);
  const askedRef = useRef(false);

  const askCleo = trpc.cleo.askOnProposal.useMutation({
    onError: (e) => toast.error(e.message || "Could not reach Cleo."),
  });
  const endSession = trpc.cleo.endLinkAAskSession.useMutation();
  const conversationHistoryQ = trpc.cleo.getProposalConversation.useQuery(
    { proposalId },
    {
      enabled: enabled && audience === "staff" && Number.isFinite(proposalId) && proposalId > 0,
    },
  );

  const endCurrentSession = (reason: "close" | "navigate") => {
    if (!enabled || endedRef.current) return;
    endedRef.current = true;
    const sessionId = sessionIdRef.current;
    const shouldPing = askedRef.current;
    askedRef.current = false;
    sessionIdRef.current = newLinkAAskSessionId();
    // PIC digest is staff Link A only — customer close must not invent a consultant notify.
    if (!shouldPing || audience !== "staff") return;
    void Promise.resolve(endSession.mutateAsync({ proposalId, sessionId, reason })).catch(() => {});
  };

  useEffect(() => {
    if (!enabled) {
      onOpenChange(false);
      return;
    }
  }, [enabled, onOpenChange]);

  useEffect(() => {
    if (!enabled) return;
    const onLeave = () => endCurrentSession("navigate");
    window.addEventListener("pagehide", onLeave);
    return () => {
      window.removeEventListener("pagehide", onLeave);
      endCurrentSession("navigate");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, proposalId]);

  useEffect(() => {
    if (historyLoadedRef.current || !conversationHistoryQ.data?.length) return;
    historyLoadedRef.current = true;
    const loaded = conversationHistoryQ.data.flatMap((r) => [
      { role: "user" as const, text: r.question },
      { role: "assistant" as const, text: r.response },
    ]);
    setCleoMessages(loaded);
  }, [conversationHistoryQ.data]);

  const sendCleoQuestion = async (q: string, proposalItemId?: number) => {
    const text = q.trim();
    if (!text || !enabled || !Number.isFinite(proposalId) || proposalId <= 0) return;
    if (askCleo.isPending) return;
    if (proposalItemId && proposalItemId > 0) {
      lastItemIdRef.current = proposalItemId;
    } else if (!shouldAttachStickyProposalItemId(text)) {
      lastItemIdRef.current = undefined;
    }
    const itemId = resolveAskCleoProposalItemId({
      question: text,
      explicitProposalItemId: proposalItemId,
      stickyProposalItemId: lastItemIdRef.current,
    });
    endedRef.current = false;
    askedRef.current = true;
    setCleoMessages((prev) => [...prev, { role: "user", text }]);
    try {
      const res = await askCleo.mutateAsync({
        proposalId,
        question: text,
        sessionId: sessionIdRef.current,
        ...(itemId && itemId > 0 ? { proposalItemId: itemId } : {}),
      });
      setCleoMessages((prev) => [...prev, { role: "assistant", text: res.response }]);
    } catch {
      setCleoMessages((prev) =>
        prev.length > 0 && prev[prev.length - 1]?.role === "user" ? prev.slice(0, -1) : prev,
      );
    }
  };

  useEffect(() => {
    if (!enabled || !pendingAsk) return;
    if (sendingKeyRef.current === pendingAsk.key) return;
    const question = pendingAsk.question.trim();
    if (!question) {
      onPendingAskConsumed?.();
      return;
    }
    sendingKeyRef.current = pendingAsk.key;
    onOpenChange(true);
    setCleoInput("");
    onPendingAskConsumed?.();
    void sendCleoQuestion(question, pendingAsk.proposalItemId);
    // sendCleoQuestion closes over latest mutation; key-gated to avoid double send.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, pendingAsk?.key]);

  useLayoutEffect(() => {
    if (!open) return;
    const el = cleoScrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [open, cleoMessages, askCleo.isPending]);

  if (!enabled) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => {
          if (open) endCurrentSession("close");
          else endedRef.current = false;
          onOpenChange(!open);
        }}
        className="fixed bottom-5 right-5 z-[200] rounded-full shadow-lg flex items-center gap-2 pointer-events-auto proposal-no-print px-4 py-3"
        style={{ background: SALES_TEXT, color: "white" }}
        aria-label={open ? "Close Cleo" : "Ask Cleo About This Proposal"}
        data-testid="proposal-ask-cleo-launcher"
      >
        {open ? (
          <X className="h-5 w-5" />
        ) : (
          <>
            <MessageCircle className="h-5 w-5" />
            <span className="text-sm font-medium hidden sm:inline">Ask Cleo</span>
          </>
        )}
      </button>

      {open ? (
        <div
          className="pointer-events-auto fixed bottom-20 right-5 z-[200] w-[min(100vw-2rem,28rem)] rounded-xl border border-[#1A2744]/15 bg-white shadow-xl flex flex-col h-[min(40rem,82vh)] proposal-no-print"
          data-testid="proposal-cleo-panel"
        >
          <div className="px-4 py-3 border-b border-[#1A2744]/10 flex items-center justify-between shrink-0">
            <div>
              <p className="font-medium text-sm" style={{ color: SALES_TEXT }}>
                Ask Cleo About This Proposal
              </p>
              <p className="text-xs text-[#1A2744]/50">Ask me anything about what's included</p>
            </div>
            <button
              type="button"
              onClick={() => {
                endCurrentSession("close");
                onOpenChange(false);
              }}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div ref={cleoScrollRef} className="p-3 flex-1 min-h-0 overflow-y-auto space-y-2 text-sm">
            {cleoMessages.length === 0 && !askCleo.isPending ? (
              <div className="text-center py-6 space-y-3">
                <MessageCircle className="h-8 w-8 mx-auto text-[#1A2744]/20" />
                <p className="text-[#1A2744]/50 text-sm">
                  {audience === "customer"
                    ? "Ask me about any product, system, or option in this proposal."
                    : "Ask me about any product, system, or option in your proposal."}
                </p>
                <div className="flex flex-wrap gap-1.5 justify-center">
                  {["What does this system do?", "Why is this included?", "What are my options?"].map((q) => (
                    <button
                      key={q}
                      type="button"
                      className="text-xs px-2.5 py-1.5 rounded-full border border-[#1A2744]/15 text-[#1A2744]/70 hover:bg-[#1A2744]/5 transition-colors"
                      onClick={() => {
                        setCleoInput("");
                        void sendCleoQuestion(q);
                      }}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            {cleoMessages.map((m, idx) => (
              <div
                key={`c-${idx}-${m.role}-${m.text.slice(0, 24)}`}
                className={
                  m.role === "user"
                    ? "ml-4 rounded-lg bg-[#1A2744]/9 px-3 py-2 text-[#1A2744]"
                    : "mr-2 rounded-lg bg-[#FAFAF7] border border-[#1A2744]/10 px-3 py-2 text-[#1A2744]/90"
                }
              >
                {m.text}
              </div>
            ))}
            {askCleo.isPending ? (
              <p className="text-xs text-[#1A2744]/50 px-1" aria-live="polite">
                Cleo is thinking...
              </p>
            ) : null}
          </div>
          <div className="p-3 border-t border-[#1A2744]/10 space-y-3 shrink-0 bg-white rounded-b-xl">
            <form
              className="flex gap-2 items-center"
              onSubmit={(e) => {
                e.preventDefault();
                if (askCleo.isPending) return;
                const t = cleoInput.trim();
                if (!t) return;
                setCleoInput("");
                void sendCleoQuestion(t);
              }}
            >
              <Input
                value={cleoInput}
                onChange={(e) => setCleoInput(e.target.value)}
                placeholder="Ask about this proposal..."
                className="h-10 text-sm flex-1 min-w-0"
                disabled={askCleo.isPending}
                autoComplete="off"
              />
              <Button
                type="submit"
                size="sm"
                className="h-10 px-4 shrink-0"
                disabled={askCleo.isPending || !cleoInput.trim()}
              >
                Send
              </Button>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
}
